// Dismissing a notification (Round 6, 21 Oct): every notification can be dismissed, whatever stands behind it. A
// dismissal takes it off the list and off the counts, and never changes the thing itself: the step stays blocked, the
// question stays open, the task stays undone. Dismissals are kept on the account, each with when it was made, so they
// hold on every device. One comes back only when something newer happens on the same thing (an agent's new update on
// the step), never by itself, however long the thing stays as it is: a dismissal is kept a year, the newest 1,000,
// and the notification centre's Dismissed view shows the last 30 days. Each device sends what it dismissed and what
// it brought back, and the board merges them, so two devices never undo each other.
//   n:<notice id>   a notice in the notification centre (a reminder, a widget alert, an agent that needs you)
//   q:<entry id>    a plan's open question
//   b:<page id>     a plan's blocked step
//   s:<session id>  a session building a plan that waits on the user

export type Dismissed = Record<string, number>;

/** How far back the Dismissed view looks. */
export const DISMISS_DAYS = 30;
/** How long a dismissal is kept, and how many. */
const KEEP_DAYS = 365;
const MAX = 1000;
const KEY = /^[nqbs]:[\w.:-]{1,120}$/;

/** The account's dismissals as the board keeps them: known kinds of key, real times, the last year, the newest 1,000. */
export function cleanDismissed(raw: unknown, now = Date.now()): Dismissed {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const keep = Object.entries(raw as Record<string, unknown>).filter(
    (e): e is [string, number] => KEY.test(e[0]) && typeof e[1] === "number" && Number.isFinite(e[1]) && e[1] > now - KEEP_DAYS * 86_400_000 && e[1] < now + 3_600_000,
  );
  return Object.fromEntries(keep.sort((a, b) => b[1] - a[1]).slice(0, MAX));
}

/**
 * The account's dismissals after one device's change: what it brought back taken out first, then what it sent laid
 * over the rest, the later time kept for each. A device's older copy never takes away what another dismissed.
 */
export function mergeDismissed(stored: unknown, sent: unknown, removed: unknown, now = Date.now()): Dismissed {
  const base = cleanDismissed(stored, now);
  for (const k of Array.isArray(removed) ? removed.slice(0, MAX) : []) if (typeof k === "string") delete base[k];
  for (const [k, at] of Object.entries(cleanDismissed(sent, now))) base[k] = Math.max(base[k] ?? 0, at);
  return cleanDismissed(base, now);
}

/** Whether a notification is dismissed: dismissed at or after the newest thing that happened on what it is about. */
export function isDismissed(d: Dismissed | null | undefined, key: string, newest = 0): boolean {
  const at = d?.[key];
  return at !== undefined && at >= newest;
}

/** The plan question a notice raised for it is about ("question:<entry id>"), or null. */
export const questionOfNotice = (key: string) => (key.startsWith("question:") ? key.slice(9) : null);
